import {NoopLogger} from "@cm/log"
import type {CmLogger} from "@cm/log"
import type {Catalog, CatalogDiff, CatalogItem, CatalogPrice, GetSubgraphCallback} from "#pricing/catalogs/catalog-interface"
import {ConfigurationList} from "#pricing/nodes/configuration-list"
import {Currency} from "#pricing/types/currency"
import type {PriceEntry, PriceMap} from "#pricing/types/pricing-types"
import {PricedItemNode} from "#pricing/nodes/priced-item-node"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"
import type {utils, WorkSheet} from "xlsx"

export class DelvauxCatalog implements Catalog {
    items: DelvauxCatalogItem[]

    constructor(items: DelvauxCatalogItem[]) {
        this.items = items
    }

    search(query: string): CatalogItem[] {
        const lowerCaseQuery = query.toLowerCase()
        return this.items.filter((item) => item.description.toLowerCase().includes(lowerCaseQuery) || item.uniqueId.toLowerCase().includes(lowerCaseQuery))
    }

    getPriceMap(): PriceMap {
        const priceMap: PriceMap = new Map()
        this.items.forEach((item) => priceMap.set(item.uniqueId, item.prices))
        return priceMap
    }

    getAllPrices(): CatalogPrice[] {
        const result: CatalogPrice[] = []
        this.items.forEach((item) => {
            item.prices.forEach(({price, priceIncludesVat}, currency) => {
                result.push({uniqueId: item.uniqueId, price, currency, priceIncludesVat})
            })
        })
        return result
    }

    diff(_newCatalog: Catalog): CatalogDiff {
        throw new Error("DelvauxCatalog.diff is not implemented")
    }
}

class DelvauxCatalogItem implements CatalogItem {
    model: string
    type: string
    prices: Map<Currency, PriceEntry>

    constructor(model: string, type: string, prices: Map<Currency, PriceEntry>) {
        this.model = model
        this.type = type
        this.prices = prices
    }

    get uniqueId() {
        return (this.model + this.type).replace(/\s+/g, "")
    }

    get description() {
        return `${this.model.trim()}, ${this.type.trim()}`
    }

    createPriceGraph(getSubgraph: GetSubgraphCallback, withCondition = true): PricedItemNode {
        return new PricedItemNode({
            parameters: {
                description: this.description,
                uniqueId: this.uniqueId,
                condition: withCondition
                    ? new VariantConditionNode({
                          parameters: {
                              condition: {variantIds: [], variantOperator: "AND", negated: false},
                              currentConfigurations: new ConfigurationList({parameters: {list: []}}),
                          },
                      })
                    : undefined,
                subPrices: undefined,
                sku: this.uniqueId,
                dependsOnSubprice: false,
                amount: 1,
            },
        })
    }
}

const COL_MODEL = 4 // E
const COL_TYPE = 6 // G

const PRICE_COLUMNS: {col: number; taxLabel: string; header: string; currency: Currency; priceIncludesVat: boolean}[] = [
    {col: 7, taxLabel: "TAX INCL FINAL FW25 PRICE", header: "EU FW25 Retail Price*", currency: Currency.Eur, priceIncludesVat: true},
    {col: 8, taxLabel: "Tax exclu FINAL FW25 PRICE", header: "USA retail price $", currency: Currency.Usd, priceIncludesVat: false},
    {col: 9, taxLabel: "Tax exclu FINAL FW25 PRICE", header: "Canada retail price CAD", currency: Currency.Cad, priceIncludesVat: false},
    {col: 10, taxLabel: "TAX INCL FINAL FW25 PRICE", header: "Japan retail price JPY", currency: Currency.Jpy, priceIncludesVat: true},
    {col: 11, taxLabel: "Tax exclu FINAL FW25 PRICE", header: "Hong Kong retail price HKD", currency: Currency.Hkd, priceIncludesVat: false},
    {col: 12, taxLabel: "Tax exclu FINAL FW25 PRICE", header: "Macau retail price MOP", currency: Currency.Mop, priceIncludesVat: false},
    {col: 13, taxLabel: "Tax Inclu FINAL FW25 PRICE", header: "Singapore retail price SGD", currency: Currency.Sgd, priceIncludesVat: true},
    {col: 14, taxLabel: "Tax Inclu FINAL FW25 PRICE", header: "Malaysia retail price MYR", currency: Currency.Myr, priceIncludesVat: true},
    {col: 15, taxLabel: "Tax Inclu FINAL FW25 PRICE", header: "Thailand retail price THB", currency: Currency.Thb, priceIncludesVat: true},
    {col: 16, taxLabel: "Tax Inclu FINAL FW25 PRICE", header: "China retail price CNY", currency: Currency.Cny, priceIncludesVat: true},
    {col: 17, taxLabel: "Tax Inclu FINAL FW25 PRICE", header: "Korea retail price KRW", currency: Currency.Krw, priceIncludesVat: true},
    {col: 18, taxLabel: "Tax Inclu FINAL FW25 PRICE", header: "Taiwan retail price TWD", currency: Currency.Twd, priceIncludesVat: true},
    {col: 19, taxLabel: "Tax Inclu FINAL FW25 PRICE", header: "UK retail price £", currency: Currency.Gbp, priceIncludesVat: true},
    {col: 20, taxLabel: "Tax Inclu FINAL FW25 PRICE", header: "Dubaï retail price AED", currency: Currency.Aed, priceIncludesVat: true},
    {col: 21, taxLabel: "Tax Inclu FINAL FW25 PRICE", header: "Saudi retail price SAR", currency: Currency.Sar, priceIncludesVat: true},
    {col: 22, taxLabel: "FINAL FW25 PRICE", header: "Switzerland retail price CHF", currency: Currency.Chf, priceIncludesVat: false},
]

const expectedHeaders: {row: number; col: number; title: string}[] = [
    ...PRICE_COLUMNS.map(({col, taxLabel}) => ({row: 2, col, title: taxLabel})),
    {row: 3, col: COL_MODEL, title: "Main shape model"},
    {row: 3, col: COL_TYPE, title: "Leather Type"},
    ...PRICE_COLUMNS.map(({col, header}) => ({row: 3, col, title: header})),
]

export function parseXlsx(xlsxBuf: Uint8Array, xlsx: typeof import("xlsx"), logger: CmLogger = NoopLogger): Catalog {
    const workbook = xlsx.read(xlsxBuf, {type: "buffer", cellDates: true, cellStyles: true, cellNF: true, cellText: true})
    const sheetName = workbook.SheetNames[0]
    const worksheet = workbook.Sheets[sheetName]

    const headerRowIndex = 3

    verifyXlsx(worksheet, expectedHeaders, xlsx)

    const range = xlsx.utils.decode_range(worksheet["!ref"]!)
    range.s.r = headerRowIndex + 1
    range.s.c = 0 // force range to start at column A so sheet_to_json array indices match absolute sheet columns

    const newRange = xlsx.utils.encode_range(range)
    const rows = xlsx.utils.sheet_to_json<unknown[]>(worksheet, {
        range: newRange,
        header: 1,
        blankrows: false,
    })

    const filledRows = rows.filter((row) => row[COL_MODEL] != null && row[COL_TYPE] != null && PRICE_COLUMNS.every(({col}) => row[col] != null))

    logger.log(`Skipped ${rows.length - filledRows.length} empty rows`)

    const catalogItems = filledRows.map(
        (row) =>
            new DelvauxCatalogItem(
                String(row[COL_MODEL]).trim(),
                String(row[COL_TYPE]).trim(),
                new Map(PRICE_COLUMNS.map(({col, currency, priceIncludesVat}) => [currency, {price: Number(row[col]), priceIncludesVat}])),
            ),
    )

    verifyData(catalogItems)

    return new DelvauxCatalog(catalogItems)
}

function verifyXlsx(worksheet: WorkSheet, headers: {row: number; col: number; title: string}[], xlsx: {utils: typeof utils}) {
    headers.forEach(({row, col, title}) => {
        const cellValue = worksheet[xlsx.utils.encode_cell({r: row, c: col})]?.v?.toString().trim()
        if (cellValue !== title) throw new Error(`Expected ${title} in cell ${xlsx.utils.encode_col(col)}${row + 1}, but got ${cellValue}`)
    })
}

function verifyData(catalogItems: DelvauxCatalogItem[]) {
    const uniqueIds = new Set<string>()
    catalogItems.forEach((item) => {
        if (uniqueIds.has(item.uniqueId)) throw new Error(`Duplicate uniqueId: ${item.uniqueId}`)
        uniqueIds.add(item.uniqueId)
    })
}
